import { environment } from '@environments/environment';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ApiHelperService } from '@api/common/services/api-helper.service';
import { ApiUrlFactory } from '@api/common/factories/api-url.factory';
import { CollectionRequest } from '@api/common/requests/collection.request';
import { map, Observable } from 'rxjs';
import { CollectionResponse } from '@api/common/responses/collection.response';
import { SequenceSearchResultCollectionItemResponse } from '@api/sequence/responses/sequence-search-result-collection-item.response';
import { SequenceSearchResultFilterCollectionRequest } from '@api/sequence/filters/sequence-search-result-filter-collection.request';
import { Id } from '@shared/types/id';
import { ApiParams } from '@environments/enums/api-params';
import { SequenceSearchResultDetailsResponse } from '@api/sequence/responses/sequence-search-result-details.response';
import { SequenceSearchResultHitCollectionItemResponse } from '@api/sequence/responses/sequence-search-result-hit-collection-item.response';

const { SEQUENCES } = environment.api;

@Injectable({
  providedIn: 'root',
})
export class SequenceSearchResultApiService {
  constructor(
    private readonly http: HttpClient,
    private readonly helper: ApiHelperService,
    private readonly urlFactory: ApiUrlFactory
  ) {}

  collection(
    request: CollectionRequest<SequenceSearchResultFilterCollectionRequest>
  ): Observable<
    CollectionResponse<SequenceSearchResultCollectionItemResponse>
  > {
    const {
      SEARCH_RESULTS: { LIST },
      HOST,
    } = SEQUENCES;
    const url = this.urlFactory.complex(builder =>
      builder
        .host(HOST)
        .path(LIST)
        .queryParams(
          this.helper.collectionRequestToQueryParams({
            request,
          })
        )
        .build()
    );

    return this.http
      .get(url)
      .pipe(
        map(
          this.helper.bodyToCollectionResponse(
            SequenceSearchResultCollectionItemResponse
          )
        )
      );
  }

  hits(
    searchResultId: Id,
    request: CollectionRequest
  ): Observable<
    CollectionResponse<SequenceSearchResultHitCollectionItemResponse>
  > {
    const {
      SEARCH_RESULTS: { HITS },
      HOST,
    } = SEQUENCES;
    const url = this.urlFactory.complex(builder =>
      builder
        .host(HOST)
        .path(HITS)
        .params({
          [ApiParams.ResultId]: searchResultId,
        })
        .queryParams(
          this.helper.collectionRequestToQueryParams({
            request,
          })
        )
        .build()
    );

    return this.http
      .get(url)
      .pipe(
        map(
          this.helper.bodyToCollectionResponse(
            SequenceSearchResultHitCollectionItemResponse
          )
        )
      );
  }

  details(id: Id): Observable<SequenceSearchResultDetailsResponse> {
    const {
      SEARCH_RESULTS: { DETAILS },
      HOST,
    } = SEQUENCES;
    const url = this.urlFactory.withParams(HOST, DETAILS, {
      [ApiParams.ResultId]: String(id),
    });

    return this.http
      .get(url)
      .pipe(
        map(this.helper.bodyToResponse(SequenceSearchResultDetailsResponse))
      );
  }
}
